import React, { useState, useEffect, useMemo } from 'react'
import { Box } from '@material-ui/core'

import { useSelector, useDispatch } from 'react-redux'
import { useStyles } from './styles'

import {
    Modal,
    Input,
    Picker,
    Button
} from 'web/src/app/root/components/common'

import { SystemTypeSelect } from '../system-type-select'

import cl from 'classnames'
import { OperationModeSelect } from '../operation-mode-select'
import {
    DEVICE_TYPE,
    getDeviceSleepTimeFrames
} from 'web/src/app/root/constants.js'
import * as actions from '../../actions'

import { enqueueSnackbar } from 'web/src/app/root/actions'
import { isTomDevice } from 'web/src/app/root/helpers/is-tom-device.js'
import { hasHeartbeatIntervalSetting } from '../../helpers/has-heartbeat-interval-setting.js'

import { UserPermissions } from 'web/src/services/permissions'

export const DeviceNameAndBatteryModal = ({
    isModalOpen,
    setIsModalOpen,
    setIsConfirmModal
}) => {
    const dispatch = useDispatch()

    const { locale } = useSelector(state => state.main)
    const { device } = useSelector(state => state.devices)

    const permissions = {
        updateDevice:
            (UserPermissions.device(device && device.customer_id).update &&
                device?.is_assigned) ||
            (device?.is_original && UserPermissions.isAllOriginalGranted())
    }
    const classes = useStyles()
    const [name, setName] = useState(device ? device.name : '')
    const [description, setDescription] = useState(
        device ? device.description : ''
    )
    const [nextSleepTimeFrame, setNextSleepTimeFrame] = useState(
        device
            ? device.next_sleep_time_frame
            : Object.keys(getDeviceSleepTimeFrames(locale))[0]
    )
    const [systemType, setSystemType] = useState(device?.pest_control_system_id)
    const [newOperationMode, setNewOperationMode] = useState(null)

    const isTom = useMemo(
        () => isTomDevice(device?.device_type),
        [device?.device_type]
    )
    const hasHeartbeatInterval = useMemo(
        () =>
            hasHeartbeatIntervalSetting(
                device?.device_type,
                device?.transfer_mode
            ),
        [device?.device_type, device?.transfer_mode]
    )

    useEffect(() => {
        setName(device ? device.name : '')
        setSystemType(device ? device.pest_control_system_id : null)
        setDescription(device ? device.description : '')
        setNextSleepTimeFrame(
            device
                ? device.next_sleep_time_frame
                : Object.keys(getDeviceSleepTimeFrames(locale))[0]
        )
    }, [device])

    const isValid = data => {
        let ok = true
        if (data.name.length < 3) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: locale.devices.nameIsTooShort,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
        return ok
    }

    const save = () => {
        const data = {
            name,
            description
        }
        if (hasHeartbeatInterval) {
            data.next_sleep_time_frame = nextSleepTimeFrame
        }
        if (device?.device_type !== DEVICE_TYPE.trapsensor) {
            data.pest_control_system_id = systemType
            data.operation_mode = newOperationMode || device.operation_mode
        }
        if (!isValid(data)) {
            return
        }

        if (newOperationMode && newOperationMode !== device.operation_mode) {
            setIsConfirmModal(true)
        }

        dispatch(actions.updateDeviceRequest(device.id, data))
    }

    return (
        <Modal open={isModalOpen} setOpen={setIsModalOpen}>
            <Box className={classes.sectionsWrapper}>
                <Box className={classes.detailsWrapper}>
                    <Input
                        className={classes.input}
                        label={locale.common.name}
                        type="text"
                        setValue={setName}
                        value={name}
                        onEnter={save}
                        disabled={!permissions.updateDevice}
                    />
                    <Input
                        className={classes.input}
                        label={locale.common.description}
                        type="text"
                        setValue={setDescription}
                        value={description}
                        onEnter={save}
                        disabled={!permissions.updateDevice}
                    />

                    {hasHeartbeatInterval ? (
                        <Box className={classes.section}>
                            <Box>{locale.devices.heartbeatInterval}:</Box>
                            <Picker
                                className={classes.select}
                                disabled={!permissions.updateDevice}
                                onChange={setNextSleepTimeFrame}
                                value={nextSleepTimeFrame}
                                options={getDeviceSleepTimeFrames(locale)}
                            />
                        </Box>
                    ) : null}
                    {!device.blocks_operation_mode_and_pest_control_system_change && (
                        <>
                            {isTom && permissions.updateDevice && (
                                <Box className={classes.section}>
                                    <Box>{locale.tom.operationMode}:</Box>
                                    <OperationModeSelect
                                        operationMode={device.operation_mode}
                                        onChange={setNewOperationMode}
                                    />
                                </Box>
                            )}
                            {isTom && (
                                <Box
                                    className={cl([
                                        classes.section,
                                        classes.select
                                    ])}>
                                    <Box>
                                        {locale.devices.pestControlSystemLabel}:
                                    </Box>
                                    <SystemTypeSelect
                                        disabled={!permissions.updateDevice}
                                        systemType={systemType}
                                        onChange={setSystemType}
                                        operationMode={device.operation_mode}
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {permissions.updateDevice && (
                        <Button onClick={save}>{locale.common.save}</Button>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}
